import React, { useState } from 'react'
import {
  Collapse,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { PuiTooltip, Text } from '@pbt/pbt-ui-components'
import { Warning as WarningIcon } from '@pbt/pbt-ui-components/src/icons'

import { CollapseArrow } from '~/components/elements/CollapseArrow/CollapseArrow'

const useStyles = makeStyles(
  (theme) => ({
    cell: {
      borderBottom: 'none',
    },
    warningIcon: {
      height: '15px',
      marginBottom: theme.spacing(-0.25),
      fill: theme.colors.uiNeedsAttention,
    },
    collapse: {
      paddingLeft: theme.spacing(3),
    },
    totalCell: {
      borderBottom: 'none',
      alignItems: 'center',
    },
    root: {
      '& .MuiTableCell-root': {
        padding: 0,
      },
    },
  }),
  { name: 'FinanceDetailedFooterTable' },
)

export type TotalRowProps = {
  name: (isExpanded: boolean) => string
  value: string | 0 | undefined
}

export type RawDataProps = {
  Component?: (props: any) => JSX.Element | null
  id: string
  name?: string
  shippingWarning?: string
  value?: string | 0 | undefined
}

export interface FinanceDetailedFooterTableProps {
  id?: string
  isGlobalFooter?: boolean
  rowData: RawDataProps[]
  totalRowData: TotalRowProps
}

const FinanceDetailedFooterTable = ({
  isGlobalFooter,
  id: sectionId,
  rowData = [],
  totalRowData,
}: FinanceDetailedFooterTableProps) => {
  const classes = useStyles()

  const [totalExpanded, setTotalExpanded] = useState(false)

  type RowDataRendererProps = {
    Component?: any
    id: string
    name?: string
    shippingWarning?: string
    value?: string | 0 | undefined
  }

  const rowDataRenderer = ({
    id,
    name,
    value,
    Component,
    shippingWarning,
  }: RowDataRendererProps) =>
    (Component && (
      <Component
        isGlobalFooter={isGlobalFooter}
        key={id}
        sectionId={sectionId}
      />
    )) || (
      <Grid
        container
        justifyContent="space-between"
        key={id}
        pr={!isGlobalFooter ? 3 : 0}
      >
        <Text variant="body2">
          {name}
          {id === 'retailOrderShippingAmount' && Boolean(shippingWarning) ? (
            <PuiTooltip
              tooltipPlacement="top-end"
              tooltipText={shippingWarning}
            >
              <WarningIcon className={classes.warningIcon} />
            </PuiTooltip>
          ) : null}
        </Text>
        <Text align="right" variant="body2">
          {value}
        </Text>
      </Grid>
    )

  return (
    <Table aria-label="collapsible table" className={classes.root}>
      <TableBody>
        <TableRow>
          <TableCell className={classes.cell} colSpan={2}>
            <Collapse className={classes.collapse} in={totalExpanded}>
              {rowData.map(rowDataRenderer)}
            </Collapse>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ border: 'none' }}>
            <Grid
              container
              justifyContent="space-between"
              pr={!isGlobalFooter ? 3 : 0}
              onClick={() => setTotalExpanded(!totalExpanded)}
            >
              <Grid
                container
                item
                alignItems="center"
                md={8}
                sm={8}
                sx={{ cursor: 'pointer' }}
                wrap="nowrap"
                xl={8}
                xs={8}
              >
                <CollapseArrow expanded={totalExpanded} />
                <Text strong variant="body2">
                  {totalRowData.name(totalExpanded)}
                </Text>
              </Grid>
              <Text inline strong sx={{ cursor: 'pointer' }} variant="body2">
                {totalExpanded && totalRowData.value}
              </Text>
            </Grid>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}

export default FinanceDetailedFooterTable
